import { CommonModule, DatePipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ToastrService } from 'ngx-toastr';

import { DropdownComponent } from '../../ui/dropdown/dropdown.component';
import { TooltipComponent } from '../../ui/tootip/tootip.component';
import { LoginService } from '../../../services/login.service';
import { FeedbackService } from '../../../services/feedback.service';
import { Feedback, FeedbackStatus } from '../../../models/feedback.model';

@Component({
  selector: 'app-notification-dropdown',
  templateUrl: './notification-dropdown.component.html',
  imports: [CommonModule, RouterModule, DropdownComponent, DatePipe, TooltipComponent]
})
export class NotificationDropdownComponent {
  isOpen = false;
  isAdmin = false;
  isLoading = false;
  activeTab: FeedbackStatus = 'new';
  feedbackItems: Feedback[] = [];
  newCount = 0;
  readonly tabs: FeedbackStatus[] = ['new', 'read', 'complete'];
  private userNames = new Map<string, string>();

  private loginService = inject(LoginService);
  private feedbackService = inject(FeedbackService);
  private toastr = inject(ToastrService);

  ngOnInit() {
    this.loginService.isAuthenticatedAdmin().subscribe(isAdmin => {
      this.isAdmin = isAdmin;
      if (isAdmin) {
        this.loadNewCount();
      }
    });
  }

  toggleDropdown() {
    this.isOpen = !this.isOpen;
    if (this.isOpen && this.isAdmin) {
      this.loadFeedback();
    }
  }

  closeDropdown() {
    this.isOpen = false;
  }

  switchTab(tab: FeedbackStatus) {
    this.activeTab = tab;
    this.loadFeedback();
  }

  loadNewCount() {
    this.feedbackService.list('new').subscribe({
      next: items => this.newCount = items.length,
      error: () => {}
    });
  }

  loadFeedback() {
    this.isLoading = true;
    this.feedbackService.list(this.activeTab).subscribe({
      next: items => {
        this.feedbackItems = items;
        this.isLoading = false;
        this.loadUserNames(items.map(i => i.userId));
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  loadUserNames(userIds: string[]) {
    const missing = userIds.filter(id => id && !this.userNames.has(id));
    if (missing.length === 0) return;
    this.loginService.getAllUsers().subscribe({
      next: users => {
        users.forEach(u => this.userNames.set(u.id, u.name || u.username));
      },
      error: () => {}
    });
  }

  getUserName(userId: string): string {
    return this.userNames.get(userId) || userId.slice(0, 8) + '…';
  }

  updateStatus(item: Feedback, newStatus: FeedbackStatus) {
    const index = this.feedbackItems.indexOf(item);
    this.feedbackItems = this.feedbackItems.filter(f => f !== item);

    this.feedbackService.updateStatus(item.id, newStatus).subscribe({
      next: () => {
        this.loadNewCount();
      },
      error: () => {
        this.feedbackItems = [
          ...this.feedbackItems.slice(0, index),
          item,
          ...this.feedbackItems.slice(index)
        ];
        this.toastr.error('Could not update feedback status. Please try again.');
      }
    });
  }

  truncateUrl(url: string, maxLength = 40): string {
    if (!url) return '';
    try {
      const path = new URL(url).pathname;
      return path.length > maxLength ? path.slice(0, maxLength) + '…' : path;
    } catch {
      return url.length > maxLength ? url.slice(0, maxLength) + '…' : url;
    }
  }
}
